import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { confluenceSessionStorageInstance as sessionStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import type { GraphQLContentStatus } from './ApprovalActivity/__generated__/ApprovalActivityHistoryQuery.graphql.ts';

export const APPROVAL_ACTIVITY_PANE_ID = 'approval-activity-pane';
export const APPROVAL_ACTIVITY_PANE_WIDTH = 400;
export const APPROVAL_ACTIVITY_PANE_CONTEXT_KEY = 'confluence.approvals.activityPane.contextKey';

export type ApprovalActivityPaneContext = {
	contentId: string;
	contentStatus: GraphQLContentStatus;
};

const VALID_APPROVAL_ACTIVITY_CONTENT_STATUSES: readonly GraphQLContentStatus[] = [
	'CURRENT',
	'DRAFT',
];

export type ApprovalActivityPaneBackActionContext = ApprovalActivityPaneContext & {
	hasActiveApprovalReview: boolean;
};

export type ApprovalActivityPaneBackAction = (
	context: ApprovalActivityPaneBackActionContext,
) => void;

type ApprovalActivityPaneOpenContext = ApprovalActivityPaneContext & {
	onBack?: ApprovalActivityPaneBackAction;
	rightSidebarContext: {
		open: (panelId: string, width: number, isMinimized?: boolean, toTop?: boolean) => void;
	};
};

type ApprovalActivityPaneSyncContext = {
	isApprovalActivityEnabled: boolean;
	isSidebarApprovalActivityPaneOpen: boolean;
	contextKey: string;
	closeApprovalActivityPane: () => void;
};

type ShouldRenderApprovalActivityPaneArgs = {
	isApprovalActivityEnabled: boolean;
	isSidebarApprovalActivityPaneOpen: boolean;
	contextKey: string;
};

export const getApprovalActivityPaneContextKey = ({
	contentId,
	contentStatus,
}: ApprovalActivityPaneContext): string => `${contentId}:${contentStatus}`;

export const getApprovalActivityPaneContextFromKey = (
	contextKey: string | null,
): ApprovalActivityPaneContext | null => {
	if (!contextKey) {
		return null;
	}

	const statusSeparatorIndex = contextKey.lastIndexOf(':');
	if (statusSeparatorIndex <= 0 || statusSeparatorIndex === contextKey.length - 1) {
		return null;
	}

	const contentId = contextKey.slice(0, statusSeparatorIndex);
	const contentStatus = contextKey.slice(statusSeparatorIndex + 1);
	if (!VALID_APPROVAL_ACTIVITY_CONTENT_STATUSES.includes(contentStatus as GraphQLContentStatus)) {
		return null;
	}

	return {
		contentId,
		contentStatus: contentStatus as GraphQLContentStatus,
	};
};

// Synchronous source of truth for the ownership helpers: session storage writes
// can be async, so an immediate
// read can miss. Storage is still written for durability across reloads.
let inMemoryContextKey: string | null = null;
let inMemoryBackAction: ApprovalActivityPaneBackAction | null = null;

export const getStoredApprovalActivityPaneContextKey = (): string | null => inMemoryContextKey;

export const setStoredApprovalActivityPaneContextKey = (contextKey: string): void => {
	inMemoryContextKey = contextKey;
	sessionStorageManager.setItem(APPROVAL_ACTIVITY_PANE_CONTEXT_KEY, contextKey);
};

export const getStoredApprovalActivityPaneBackAction = ({
	contentId,
	contentStatus,
}: ApprovalActivityPaneContext): ApprovalActivityPaneBackAction | null => {
	const contextKey = getApprovalActivityPaneContextKey({ contentId, contentStatus });
	if (inMemoryContextKey !== contextKey) {
		return null;
	}
	return inMemoryBackAction;
};

const setStoredApprovalActivityPaneBackAction = (
	backAction: ApprovalActivityPaneBackAction | undefined,
): void => {
	inMemoryBackAction = backAction ?? null;
};

export const clearStoredApprovalActivityPaneContextKey = (): void => {
	inMemoryContextKey = null;
	inMemoryBackAction = null;
	sessionStorageManager.removeItem(APPROVAL_ACTIVITY_PANE_CONTEXT_KEY);
};

export const isApprovalActivityPaneOwnedByContext = ({
	contextKey,
	storedContextKey,
}: {
	contextKey: string;
	storedContextKey: string | null;
}): boolean => storedContextKey === contextKey;

export const shouldRenderApprovalActivityPaneForContext = ({
	isApprovalActivityEnabled,
	isSidebarApprovalActivityPaneOpen,
	contextKey,
}: ShouldRenderApprovalActivityPaneArgs): boolean => {
	if (!isApprovalActivityEnabled || !isSidebarApprovalActivityPaneOpen) {
		return false;
	}

	return isApprovalActivityPaneOwnedByContext({
		contextKey,
		storedContextKey: getStoredApprovalActivityPaneContextKey(),
	});
};

export const syncApprovalActivityPaneContextOwnership = ({
	isApprovalActivityEnabled,
	isSidebarApprovalActivityPaneOpen,
	contextKey,
	closeApprovalActivityPane,
}: ApprovalActivityPaneSyncContext): void => {
	// When disabled while the pane is still open, close it so it can't linger
	// with stale content; always clear ownership so a re-enable can't match it.
	if (!isApprovalActivityEnabled) {
		if (isSidebarApprovalActivityPaneOpen) {
			closeApprovalActivityPane();
		}
		clearStoredApprovalActivityPaneContextKey();
		return;
	}

	if (!isSidebarApprovalActivityPaneOpen) {
		clearStoredApprovalActivityPaneContextKey();
		return;
	}

	const storedContextKey = getStoredApprovalActivityPaneContextKey();
	if (isApprovalActivityPaneOwnedByContext({ contextKey, storedContextKey })) {
		return;
	}

	closeApprovalActivityPane();
	clearStoredApprovalActivityPaneContextKey();
};

export const openApprovalActivityPaneForContext = ({
	contentId,
	contentStatus,
	onBack,
	rightSidebarContext,
}: ApprovalActivityPaneOpenContext): void => {
	if (fg('cc_approvals_activity_log_edit_mode')) {
		setStoredApprovalActivityPaneContextKey(
			getApprovalActivityPaneContextKey({ contentId, contentStatus }),
		);
		if (
			(onBack || inMemoryBackAction !== null) &&
			expValEquals('confluence_approvals_activity_back_behavior_update', 'isEnabled', true)
		) {
			setStoredApprovalActivityPaneBackAction(onBack);
		}
		rightSidebarContext.open(APPROVAL_ACTIVITY_PANE_ID, APPROVAL_ACTIVITY_PANE_WIDTH);
		return;
	}

	rightSidebarContext.open(APPROVAL_ACTIVITY_PANE_ID, APPROVAL_ACTIVITY_PANE_WIDTH);
	setStoredApprovalActivityPaneContextKey(
		getApprovalActivityPaneContextKey({ contentId, contentStatus: 'CURRENT' }),
	);
	if (
		(onBack || inMemoryBackAction !== null) &&
		expValEquals('confluence_approvals_activity_back_behavior_update', 'isEnabled', true)
	) {
		setStoredApprovalActivityPaneBackAction(onBack);
	}
};
